import tokens from '@kluein/design-tokens';

import type { BattlecardsType, CardType } from 'api/api.types';

type SizeProps = {
  heading: {
    lineHeight: string;
    fontSize: string;
  };
  sidebar: {
    width: string;
  };
  battlecardContainer: {
    width: string;
  };
  battlecardContainerCollapsed: {
    width: string;
  };
  profileContainer: {
    width: string;
  };
  profileContainerCollapsed: {
    width: string;
  };
  navButton: {
    width: string;
  };
};

export const collapsedSidebarWidth = '12px';
export const pageMargin = '12px';
export const sidebarWidth = '245px';
export const sidebarWidthSmaller = '190px';

// Returns a fresh default SizeProps for the largest breakpoint (>= 1750px)
function getDefaultSizeProps(): SizeProps {
  return {
    heading: {
      lineHeight: tokens.lineHeight.dense,
      fontSize: tokens.fontSize.headingFont.medium,
    },
    sidebar: {
      width: sidebarWidth,
    },
    battlecardContainer: {
      width: `calc(100% - ${sidebarWidth} - ${pageMargin})`,
    },
    battlecardContainerCollapsed: {
      width: `calc(100% - ${collapsedSidebarWidth} - ${pageMargin})`,
    },
    profileContainer: {
      width: `calc(100% - ${sidebarWidth})`,
    },
    profileContainerCollapsed: {
      width: '100%',
    },
    navButton: {
      width: '221px',
    },
  };
}

// Shared overrides for small breakpoints where the sidebar is an overlay
function getSmallScreenOverrides(): Pick<
  SizeProps,
  | 'battlecardContainer'
  | 'battlecardContainerCollapsed'
  | 'profileContainer'
  | 'profileContainerCollapsed'
> {
  return {
    battlecardContainer: { width: `calc(100% - ${pageMargin})` },
    battlecardContainerCollapsed: { width: `calc(100% - ${pageMargin})` },
    // Expanded sidebar is an overlay, does not affect content width
    profileContainer: { width: '100%' },
    profileContainerCollapsed: { width: '100%' },
  };
}

export function resizeScreen(screenSize: Record<string, boolean>): SizeProps {
  // Below 412px
  if (!screenSize.xxsmall) {
    return {
      ...getDefaultSizeProps(),
      heading: {
        lineHeight: tokens.lineHeight.xxdense,
        fontSize: tokens.fontSize.bodyFont.medium,
      },
      sidebar: { width: sidebarWidth },
      ...getSmallScreenOverrides(),
    };
  }

  // Below 562px
  if (!screenSize.xsmall) {
    return {
      ...getDefaultSizeProps(),
      heading: {
        lineHeight: tokens.lineHeight.dense,
        fontSize: tokens.fontSize.headingFont.small,
      },
      sidebar: { width: sidebarWidth },
      ...getSmallScreenOverrides(),
    };
  }

  // Below 768px
  if (!screenSize.small) {
    return {
      ...getDefaultSizeProps(),
      heading: {
        lineHeight: tokens.lineHeight.dense,
        fontSize: tokens.fontSize.headingFont.medium,
      },
      sidebar: { width: sidebarWidth },
      navButton: { width: '144px' },
      ...getSmallScreenOverrides(),
    };
  }

  // Below 1024px
  if (!screenSize.medium) {
    return {
      ...getDefaultSizeProps(),
      heading: {
        lineHeight: tokens.lineHeight.dense,
        fontSize: tokens.fontSize.headingFont.large,
      },
      sidebar: { width: sidebarWidthSmaller },
      navButton: { width: '176px' },
      ...getSmallScreenOverrides(),
    };
  }

  // Below 1340px
  if (!screenSize.large) {
    return {
      ...getDefaultSizeProps(),
      heading: {
        lineHeight: tokens.lineHeight.dense,
        fontSize: tokens.fontSize.headingFont.large,
      },
      sidebar: { width: sidebarWidth },
      navButton: { width: '176px' },
      battlecardContainer: {
        width: `calc(100% - ${sidebarWidth} - ${pageMargin})`,
      },
      battlecardContainerCollapsed: {
        width: `calc(100% - ${collapsedSidebarWidth} - ${pageMargin})`,
      },
      profileContainer: { width: `calc(100% - ${sidebarWidth})` },
      profileContainerCollapsed: {
        width: `calc(100% - ${collapsedSidebarWidth})`,
      },
    };
  }

  // Below 1750px
  if (!screenSize.xlarge) {
    return {
      ...getDefaultSizeProps(),
      heading: {
        lineHeight: tokens.lineHeight.dense,
        fontSize: tokens.fontSize.headingFont.large,
      },
      profileContainerCollapsed: {
        width: `calc(100% - ${collapsedSidebarWidth})`,
      },
    };
  }

  // Above 1750px — fresh defaults
  return getDefaultSizeProps();
}

export function getMostRecentActionDate(battlecard: Partial<BattlecardsType>): {
  date: Date;
  type: 'updated' | 'reviewed';
} {
  const updatedAt = new Date(battlecard.updatedAt || 0);
  const reviewedAt = new Date(battlecard.review?.reviewedAt || 0);

  const type =
    updatedAt.getTime() > reviewedAt.getTime() ? 'updated' : 'reviewed';

  return {
    date: type === 'updated' ? updatedAt : reviewedAt,
    type,
  };
}

export function getMostRecentCardActionDate(card: Partial<CardType>): {
  author?: number;
  date: Date;
  type: 'updated' | 'reviewed';
} {
  const updatedAt = new Date(card.updatedAt || 0);
  const reviewedAt = new Date(card.reviewer?.reviewedAt || 0);

  const type =
    updatedAt.getTime() > reviewedAt.getTime() ? 'updated' : 'reviewed';

  return {
    author: type === 'updated' ? card.author?.id : card.reviewer?.id,
    date: type === 'updated' ? updatedAt : reviewedAt,
    type,
  };
}
